import '../assets/css/FSARequestLicense.css';
import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import UserContext from "../components/common/UserContext";
import {useLocalization} from "@progress/kendo-react-intl";
import {useHistory} from "react-router-dom";
import {
    fsaLicensingKey,
    hostPendingSpecialKey,
    mainMessages,
    navigationAlertKey,
    productsHostKey,
    requestLicensesKey,
    selectProductsKey
} from "../assets/text/MultilingualText";
import Spinner from "../components/common/Spinner";
import PageHeader from "../components/common/PageHeader";
import {Text} from "../components/common/MultilingualText";
import KSMStepper from "../components/common/Stepper";
import SelectProducts from "../components/FSARequestLicense/SelectProducts";
import {
    AssignedProductContext,
    GeneralContext,
    SearchProductContext,
    SelectedProductContext
} from "../components/FSARequestLicense/FSARequestLicenseContext";
import {process} from "@progress/kendo-data-query";
import {addDays} from "@progress/kendo-date-math";
import {capitalizeSentence} from "../components/common/utilities";
import AssignProducts from "../components/FSARequestLicense/AssignProducts";
import RequestDownload from "../components/FSARequestLicense/RequestDownload";
import axios from "axios";
import config from "../config";
import {convertDateFormat} from "../components/common/Grid";
import SummaryChart from "../components/FSARequestLicense/SummaryChart";
import moment from "moment/moment";
import uuid from "react-uuid";
import {useSupportContext} from "../components/common/SupportContext";

const initialGridState = {
    take: 10, skip: 0,
};

function FSARequestLicense(props) {
    const {
        siteLanguage,
        siteLanguageDefault,
        accessToken
    } = useContext(UserContext);
    const { dispatch } = useSupportContext();
    const localization = useLocalization();
    let history = useHistory();

    const [isLoading, setIsLoading] = useState(false);

    const defaultStepperItems = [
        {
            label: localization.toLanguageString(selectProductsKey, mainMessages[siteLanguageDefault][selectProductsKey]),
            number: 1,
            disabled: false
        },
        {
            label: localization.toLanguageString(productsHostKey, mainMessages[siteLanguageDefault][productsHostKey]),
            number: 2,
            disabled: true
        },
        {
            label: localization.toLanguageString(requestLicensesKey, mainMessages[siteLanguageDefault][requestLicensesKey]),
            number: 3,
            disabled: true
        },
    ];

    const [stepperItems, setStepperItems] = useState(defaultStepperItems);
    const [stepValue, setStepValue] = useState(0);

    const changeStepActivation = (stepChange) => {
        if (stepValue === 0) {
            saveSelectionChanges(stepChange);
            return null
        } else if (stepValue === 1) {
            saveAssignChanges(stepChange);
        } else {
            setStepValue(stepChange);
        }
    };

    useEffect(() => {
        setStepperItems(defaultStepperItems);
    }, [siteLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    // add listeners for changing site navigation (back, forward) or unloading resources (close tab, window, or refresh)
    // remove listeners when leaving page
    useEffect(() => {
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    //Sets the return value for before unload use effect. Return value can be a string.
    //This needs to be useCallback so React memorizes the function on every rerender
    const handleBeforeUnload = useCallback((e) => {
        e.preventDefault();
        e.returnValue = true;
    }, []);

    //Confirm exiting page that is not caused by redirect or react router push
    const [afterOnLoad, setAfterOnLoad] = useState(false)
    const currentURL = window.location.pathname + window.location.search

    useEffect(() => {
        //Returning false from the callback blocks the navigation transition, returning true allows the transition to go through.
        if (afterOnLoad) {
            const unblock = history.block(() => {
                if (window.confirm(
                    localization.toLanguageString(navigationAlertKey, mainMessages[siteLanguageDefault][navigationAlertKey])
                )) {
                    return true
                } else {
                    window.history.replaceState(null, null, currentURL);
                    return false
                }
            });
            return () => {
                unblock();
            };
        }
    }, [afterOnLoad]); // eslint-disable-line react-hooks/exhaustive-deps

    //FSA Header Info
    const [headerInfo, setHeaderInfo] = useState({
        "agreement": "",
        "start_date": "",
        "end_date": "",
        "status": "ACTIVE",
        "budget": 0,
        "overdraft_budget": 0,
        "total_budget": 0,
        "consumed": 0,
        "cart": 0,
        "available": 0,
        "overdraft_consumed": 0,
        "overdraft_cart": 0,
        "overdraft_available": 0,
        "total_remaining_budget": 0,
        "fsa_id": "",
        "currency_code": "USD",
        "currency_locale": "en-US",
        "legal_disclosure": "",
        "mininum_duration": 30
    })

    //Key Standards
    const uniqueID = 'cart_id'
    const parentUniqueID = 'parent_cart_id'
    const onePerHostKey = 'redeem_one_per_host_flag'
    const productNumKey = 'product_number'
    const licenseStartDateKey = 'license_start_date'
    const licenseEndDateKey = 'license_end_date'

    //Sub Pool
    const [alreadyOverDraft, setAlreadyOverDraft] = useState(false);
    const [showOverdraft, setShowOverdraft] = useState(false);
    const [selectOverdraft, setSelectOverdraft] = useState(false);

    //Search Product States
    const [searchedProducts, setSearchedProducts] = useState([]);
    const [searchedGridState, setSearchedGridState] = useState(initialGridState);
    const [searchedDataState, setSearchedDataState] = useState(
        process(searchedProducts.map((product) => ({
            ...product,
        })), initialGridState)
    );

    //Select Product States
    const [selectedProducts, setSelectedProducts] = useState([]);

    //Select Products Grid
    const selectID = 'selected'
    const [selectedGridState, setSelectedGridState] = useState(initialGridState);
    const [selectHeaderCheck, setSelectHeaderCheck] = useState(false);

    //unsaved changes for all products
    const [unsavedChanges, setUnsavedChanges] = useState(new Set())
    const [unsavedAssignedChanges, setUnsavedAssignedChanges] = useState(new Set())

    //Assigned Products state
    const [assignedProducts, setAssignedProducts] = useState([]);

    //Assign Products Grid
    const assignSelectID = 'assignSelected'
    const [assignedGridState, setAssignedGridState] = useState(initialGridState);
    const assignedGridStateRef = useRef(initialGridState)
    const [assignedHeaderCheck, setAssignedHeaderCheck] = useState(false);
    const disableArrayID = 'license_type'
    const sortByDisableID = 'hostid_type'
    const sortByDisableID2 = 'compatibility'
    let sortByDisableValue = useRef(null)
    let sortByDisableValue2 = useRef(null)
    const [enableSortByDisable, setEnableSortByDisable] = useState(false);

    //Save changes and overdraft states
    const [showGenericError, setShowGenericError] = useState(false);
    const [showSecondGenericError, setShowSecondGenericError] = useState(false);
    const [showOnloadExceedError, setShowOnloadExceedError] = useState(false);
    const [showCannotSaveError, setShowCannotSaveError] = useState(false);
    const [showQuantityConsumedError, setShowQuantityConsumedError] = useState(false);
    const [showOverdraftModal, setShowOverdraftModal] = useState(false)
    const [overdraftStepChange  , setOverdraftStepChange] = useState(0);

    //min and max date for start/end date
    const [minDate, setMinDate] = useState(null);
    const [minDurationDate, setMinDurationDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);

    const validateDates = (item, minDate, maxDate, minDuration) => {
        item['start_date_valid'] = item[licenseStartDateKey] >= minDate
        let minEndDate = addDays(item[licenseStartDateKey], minDuration)
        if (minEndDate > maxDate) {
            minEndDate = maxDate
        }
        item['end_date_valid'] = item[licenseEndDateKey] >= minEndDate && item[licenseEndDateKey] <= maxDate
        return item
    }

    //Process new selected/assigned products.
    const preprocessNewProducts = (product, headerInfo, type = "SELECTED") => {
        let newProduct = JSON.parse(JSON.stringify(product))
        newProduct[selectID] = false //set checkbox for select products my cart
        newProduct[assignSelectID] = false //set checkbox for assigned
        newProduct.quantity = Number(newProduct.quantity)
        newProduct.disabled = false
        newProduct.currency_code = headerInfo.currency_code
        newProduct.currency_locale = headerInfo.currency_locale
        newProduct[licenseStartDateKey] = new Date(newProduct.license_start_date)
        newProduct[licenseEndDateKey] = new Date(newProduct.license_end_date)

        if (type === "SELECTED") {
            newProduct.start_date_valid = true
            newProduct.end_date_valid = true
            newProduct.qty_valid = true
            newProduct.exceedError = false
            newProduct.unassigned_quantity = Number(newProduct.quantity)
            newProduct.saved_selected_quantity = Number(newProduct.quantity)
            newProduct.saved_assigned_quantity = 0
        }

        //set assigned qty (every time, unassigned quantity changes, assigned quantity needs to be rechecked)
        if (type === "SELECTED") {
            let license = newProduct.license_type.filter(lt => lt.selected)
            newProduct.assign_quantity = license[0][onePerHostKey] === "Y" ? 1
                : newProduct.unassigned_quantity === 1 ? 1
                    : null;
        } else {
            newProduct.assign_quantity = newProduct.quantity
        }

        //Setup license type values and selected product ids
        if (type === "SELECTED") {
            for (const licenseType of newProduct.license_type) {
                licenseType.text = capitalizeSentence(licenseType.license_type, true)
                licenseType[sortByDisableID2] = 'Y'
                if (licenseType.selected) {
                    newProduct.selected_license_type_text = licenseType.text
                    newProduct.selected_product_id = licenseType.product_id
                }
            }
        } else {
            newProduct.license_type_text = capitalizeSentence(newProduct.license_type, true)
            newProduct.selected_product_id = newProduct.product_id
        }

        return newProduct
    }

    useEffect(() => {
        const onLoad = () => {
            let headers = {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken,
            };
            setIsLoading(true);

            let query = new URLSearchParams(window.location.search);
            const id = query.get('fsa_id');

            axios.get(
                config.fsa_request_license.ON_LOAD + '?id=' + id,
                {headers: headers}
            )
                .then((response) => {
                    let newHeaderInfo = response.data['header_info']

                    //set new min and max dates for selections
                    let currentDate = new Date();
                    currentDate.setHours(0, 0, 0, 0);
                    let newMinDate = new Date(newHeaderInfo.start_date)
                    if (currentDate > newMinDate) {
                        newMinDate = currentDate
                    }
                    const minDuration = newHeaderInfo?.minimum_duration ? newHeaderInfo.minimum_duration : 30
                    let newMinDurationDate = addDays(newMinDate, minDuration)
                    let newMaxDate = new Date(newHeaderInfo.end_date)

                    //TODO remove once pricing engine restrictions are removed
                    // const pricingRestriction = addDays(newMinDate, 365) //3 * 365 - 1 = 1095 = 1095 days
                    const pricingRestriction = addDays(newMinDate, 1095) //3 * 365 - 1 = 1095 = 1095 days
                    if (newMaxDate > pricingRestriction ) {
                        newMaxDate = pricingRestriction
                    }

                    if(newMinDurationDate > newMaxDate) {
                        newMinDurationDate = newMaxDate
                    }
                    setMinDate(newMinDate)
                    setMaxDate(newMaxDate)
                    setMinDurationDate(newMinDurationDate)

                    //set budget donut
                    newHeaderInfo.start_date = convertDateFormat(newHeaderInfo.start_date)
                    newHeaderInfo.end_date = convertDateFormat(newHeaderInfo.end_date)
                    setShowOverdraft(newHeaderInfo.overdraft_consumed > 0 || newHeaderInfo.overdraft_cart > 0)
                    setHeaderInfo(newHeaderInfo)
                    setShowOverdraft(newHeaderInfo.overdraft_available < newHeaderInfo.overdraft_budget)
                    setSelectOverdraft(newHeaderInfo.overdraft_available < newHeaderInfo.overdraft_budget)

                    //set overdraft flag check
                    setAlreadyOverDraft(newHeaderInfo.overdraft_consumed > 0 || newHeaderInfo.overdraft_cart > 0)

                    let newSelectedProducts = response.data['selected'] || []
                    newSelectedProducts = newSelectedProducts.map(product => {
                        product = preprocessNewProducts(product, newHeaderInfo, 'SELECTED')
                        return validateDates(product, minDate, newMaxDate, minDuration)
                    })

                    //preprocess saved assigned products
                    const newAssignedProducts = []
                    response.data['assigned'] = response.data['assigned'] || []
                    for (const product of response.data['assigned']) {
                        let newProduct = preprocessNewProducts(product, newHeaderInfo,'ASSIGNED')

                        let parentIndex = newSelectedProducts.findIndex(item => item[uniqueID] === newProduct[parentUniqueID])
                        if (parentIndex > -1) {
                            //unassigned qty already set in preprocess method
                            let newSelectedProduct = newSelectedProducts[parentIndex]
                            newSelectedProduct.unassigned_quantity -= newProduct.assign_quantity
                            newSelectedProduct.saved_assigned_quantity += newProduct.assign_quantity
                            let license = newSelectedProduct.license_type.filter(lt => lt.selected)
                            newSelectedProduct.assign_quantity = license[0][onePerHostKey] === "Y" ? 1
                                : newSelectedProduct.unassigned_quantity === 1 ? 1
                                    : null;
                        }

                        let index = newAssignedProducts.findIndex(host => host.host_id === newProduct.host_id)
                        if (index === -1) {
                            //new transaction id
                            let id = uuid();

                            // panel bar titles for assigned products
                            let isPartial = "N"; //default partial compatibility to false
                            let title = (isPartial === "P") ? localization.toLanguageString(hostPendingSpecialKey, mainMessages[siteLanguageDefault][hostPendingSpecialKey])
                                : (newProduct.file_name) ? "Host " + newProduct.file_name
                                    : (newProduct.host_id) ? "Host " + newProduct.host_id
                                        : "";

                            // add serial number to panel bar title
                            product.serial_number ? title += ", " + product.serial_number : title += "";

                            // add alias to panel bar title
                            product.alias ? title += (' "' + product.alias + '"') : title += "";

                            newAssignedProducts.push({
                                transaction_id: id,
                                is_partial: isPartial,
                                host_id: newProduct.host_id,
                                alias: newProduct.alias,
                                products: [newProduct],
                                panel_bar_title: title,
                                expanded: ['.0'],
                                icons: {
                                    checked: true,
                                },
                            })
                        } else {
                            newAssignedProducts[index].products.push(newProduct)
                        }
                    }

                    setSelectedProducts(newSelectedProducts)
                    setAssignedProducts(newAssignedProducts)

                    setIsLoading(false)
                    setAfterOnLoad(true)

                })
                .catch((error) => {
                    console.log("ERROR: Failed to GET FSA Request Licenses On Load data", error);
                    setAfterOnLoad(false);
                    history.push('/error');
                })
        }
        onLoad();
    }, [])

    const saveSelectionChanges = (stepChange, ignoreOverdraft) => {
        setShowGenericError(false)
        setShowCannotSaveError(false)
        setShowOverdraftModal(false)
        setShowOnloadExceedError(false)

        if (unsavedChanges.size === 0) {
            setStepValue(stepChange)
            return null;
        }

        let newUnsavedProducts = []
        for (const unsavedID of unsavedChanges) {
            const productIndex = selectedProducts.findIndex(item => item[uniqueID] === unsavedID)
            if (productIndex > -1) {
                const newProduct = JSON.parse(JSON.stringify(selectedProducts[productIndex]))
                newProduct['license_start_date'] = moment(newProduct[licenseStartDateKey]).format('YYYY-MM-DD HH:mm:ss')
                newProduct['license_end_date'] = moment(newProduct[licenseEndDateKey]).format('YYYY-MM-DD HH:mm:ss')
                newUnsavedProducts.push(newProduct)
            }
        }

        setIsLoading(true)
        let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };

        ignoreOverdraft = ignoreOverdraft || alreadyOverDraft ? 'Y' : 'N'

        let data = {
            "module": "FSA",
            "sub_module": "CART",
            "action": "SAVE",
            "input_json": {
                "fsa_id": headerInfo.fsa_id,
                "ignore_overdraft": ignoreOverdraft,
                "products": newUnsavedProducts
            }
        }

        axios.post(
            config.fsa_request_license.CART,
            data,
            {headers: headers}
        )
            .then(response => {

                //update budget pools
                const data = response.data || {};
                let newHeaderInfo = data['header_info']
                setHeaderInfo(newHeaderInfo)

                //preprocess saved products
                let newItems = data['cart_items']
                newItems = newItems.map((item) => {
                    return preprocessNewProducts(item, newHeaderInfo, item['status'])
                })

                const newAssignedProducts = assignedProducts.map(host => {
                    let newHost = host
                    newHost.products = newHost.products.map(product => {
                        let newProduct = product
                        newItems.forEach(newItem => {
                            if (product[uniqueID] === newItem[uniqueID]) {
                                newProduct = newItem
                            }
                        })
                        return newProduct
                    })
                    return newHost
                })

                //update existing products with new saved products
                const newSelectedProducts = selectedProducts.map(product => {
                   let newProduct = product
                    newItems.forEach(newItem => {
                        if (product[uniqueID] === newItem[uniqueID]) {
                            newProduct = newItem
                            newProduct[selectID] = product[selectID]
                            newProduct[assignSelectID] = product[assignSelectID]
                            newProduct.saved_assigned_quantity = product.saved_assigned_quantity
                            newProduct.unassigned_quantity = newProduct.quantity - newProduct.saved_assigned_quantity
                            newProduct.assign_quantity = newProduct[onePerHostKey] === "Y" ? 1
                                : newProduct.unassigned_quantity === 1 ? 1
                                    : null;
                        }
                    })
                    return newProduct
                })

                unsavedChanges.clear()
                setUnsavedChanges(unsavedChanges)
                setSelectedProducts(newSelectedProducts);
                setAssignedProducts(newAssignedProducts)
                setStepValue(stepChange);
            })
            .catch(error => {
                console.log("ERROR: Failed to save cart", error);
                let errorCode = error?.response?.data?.error_code;
                if (errorCode === 'MOD_FSA_163') { //overdraft error
                    setShowOverdraftModal(true)
                    setOverdraftStepChange(stepChange)
                }
                else if (errorCode === 'MOD_FSA_164') { // no more budget
                    setShowCannotSaveError(true)
                } else {
                    setShowGenericError(true)
                }
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    const saveAssignChanges = (stepChange) => {
        setShowGenericError(false)
        setShowQuantityConsumedError(false)

        let newUnsavedProducts = []
        for (const unsavedID of unsavedAssignedChanges) {
            assignedProducts.forEach((host) => {
                host.products.forEach((product) => {
                    if (product[uniqueID] === unsavedID) {
                        const newProduct = JSON.parse(JSON.stringify(product))
                        newProduct['license_start_date'] = moment(newProduct[licenseStartDateKey]).format('YYYY-MM-DD HH:mm:ss')
                        newProduct['license_end_date'] = moment(newProduct[licenseEndDateKey]).format('YYYY-MM-DD HH:mm:ss')
                        newProduct['quantity'] = newProduct['assign_quantity']
                        newUnsavedProducts.push(newProduct)
                    }
                })
            })
        }

        if (newUnsavedProducts.length === 0) {
            setStepValue(stepChange)
            return null
        }

        setIsLoading(true)
        let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };

        let data = {
            "module": "FSA",
            "sub_module": "CART",
            "action": "SAVE",
            "input_json": {
                "fsa_id": headerInfo.fsa_id,
                "ignore_overdraft": 'Y',
                "products": newUnsavedProducts
            }
        }

        axios.post(
            config.fsa_request_license.CART,
            data,
            {headers: headers}
        )
            .then(response => {

                //update budget pools
                const data = response.data || {};
                let newHeaderInfo = data['header_info']
                setHeaderInfo(newHeaderInfo)

                //preprocess saved products
                let newItems = data['cart_items']
                newItems = newItems.map((item) => {
                    return preprocessNewProducts(item, newHeaderInfo, item['status'])
                })

                const newAssignedProducts = assignedProducts.map(host => {
                    host.products = host.products.map(product => {
                        newItems.forEach(newItem => {
                            if (product[uniqueID] === newItem[uniqueID]) {
                                product = newItem
                                return product
                            }
                        })
                        return product
                    })
                    return host
                })

                const newSelectedProducts = selectedProducts.map(item => {
                    let newSavedAssignQuantity = 0
                    newAssignedProducts.forEach(host => {
                        host.products.forEach(product => {
                            if (product[parentUniqueID] === item[uniqueID]) {
                                newSavedAssignQuantity += product.assign_quantity
                            }
                        })
                    })

                    if (newSavedAssignQuantity > 0) {
                        item.saved_assigned_quantity = newSavedAssignQuantity
                        item.unassigned_quantity = item.quantity - newSavedAssignQuantity
                        item.assign_quantity = item[onePerHostKey] === "Y" ? 1
                            : item.unassigned_quantity === 1 ? 1
                                : null;
                    }
                    return item
                })

                unsavedAssignedChanges.clear()
                setUnsavedAssignedChanges(unsavedAssignedChanges)
                setAssignedProducts(newAssignedProducts)
                setSelectedProducts(newSelectedProducts)
                setStepValue(stepChange);

            })
            .catch(error => {
                console.log("ERROR: Failed to save cart", error);
                let errorCode = error?.response?.data?.error_code;
                if (errorCode === 'MOD_FSA_161') { //qty redeemed error
                    setShowQuantityConsumedError(true)
                }
                else {
                    setShowGenericError(true)
                }
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    // if on step 1 & 2 and there are no unassigned quantity available and assigned products then disable step 2 & 3
    useEffect(() => {
        if (stepValue !== 2) { //step 3 has controls are in license download component
            const hasErrors = selectedProducts.filter(item => 
                !item.start_date_valid || 
                !item.end_date_valid ||
                !item.qty_valid
            ).length === 0
            let selectableProducts = selectedProducts.filter((product) => product.unassigned_quantity > 0)
            let exceedBudget = headerInfo.total_remaining_budget < 0 || headerInfo.overdraft_available < 0

            stepperItems[1].disabled =
                (assignedProducts.length === 0 && selectableProducts.length === 0) ||
                (selectableProducts.length > 0 && !hasErrors) ||
                exceedBudget

            stepperItems[2].disabled =
                assignedProducts.length === 0 ||
                (selectableProducts.length > 0 && !hasErrors) ||
                exceedBudget

            setStepperItems([...stepperItems]);
        }
    }, [selectedProducts, assignedProducts, stepValue, headerInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    //close errors when changing steps
    useEffect(() => {
        setShowGenericError(false)
        setShowSecondGenericError(false)
        setShowCannotSaveError(false)
        setShowQuantityConsumedError(false)
        setShowOnloadExceedError(false)
    }, [stepValue]);

    //show overdraft
    useEffect(() => {
        if(headerInfo.overdraft_available < headerInfo.overdraft_budget) {
            setAlreadyOverDraft(true) //ignore_overdraft = 'Y'
            const newShowOverdraft = true
            setShowOverdraft(newShowOverdraft) //show overdraft donut and button
            if(newShowOverdraft && !selectOverdraft) { //already showing overdraft but normal budget is selected
                setSelectOverdraft(false) //keep normal budget donut and button selected
            }else{
                setSelectOverdraft(true) //select overdraft donut and button
            }
        } else {
            setAlreadyOverDraft(false)
            setShowOverdraft(false)
            setSelectOverdraft(false)
        }
    }, [headerInfo, showOverdraft, selectOverdraft]);

    //show onload exceed error
    useEffect(() => {
        setShowOnloadExceedError(headerInfo.total_remaining_budget < 0 || headerInfo.overdraft_available < 0)
        }, [headerInfo.total_remaining_budget, headerInfo.overdraft_available]
    );

    // useEffect(() => {
    //     console.log('searchedProducts', searchedProducts);
    // }, [searchedProducts])

    // useEffect(() => {
    //     console.log('selectedProducts', selectedProducts);
    // }, [selectedProducts])

    // useEffect(() => {
    //     console.log('assignedProducts', assignedProducts);
    // }, [assignedProducts])

    // Support Context Metadata
    // Set Module and Step values on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const EN_STEP_LABELS = [
        "Select Products",
        "Assign Products to Hosts",
        "Request Licenses"
    ]
    useEffect(() => {
        dispatch({ type: 'MODULE_LOAD', payload: {module: 'FSA Request License', step: EN_STEP_LABELS[0] }});
        let query = new URLSearchParams(window.location.search);
        const id = query.get('fsa_id');
        // Support Metadata add fsa id
        dispatch({ type: 'UPDATE_TRANSACTION', payload: {fsa_id: id}});
    }, []);
    // Set step value on change
    useEffect(() => {
        dispatch({ type: 'CHANGE_STEP', payload: EN_STEP_LABELS[stepValue]});
    }, [stepValue])
    // Update Support Metadata for assigned products
    useEffect(() => {
        if (assignedProducts.length !== 0) {
            dispatch({ type: 'UPDATE_TRANSACTION', payload: {assigned: assignedProducts}});
        }
    }, [assignedProducts])

    return (
        <>
            {isLoading ? <Spinner/> : <></>}
            <PageHeader/>
            <GeneralContext.Provider
                value={{
                    uniqueID,
                    parentUniqueID,
                    onePerHostKey,
                    productNumKey,
                    licenseStartDateKey,
                    licenseEndDateKey,
                    setIsLoading,
                    stepperItems,
                    setStepperItems,
                    handleBeforeUnload,
                    changeStepActivation,
                    preprocessNewProducts,
                    minDate,
                    minDurationDate,
                    maxDate,
                    validateDates,
                    initialGridState,
                    headerInfo,
                    setHeaderInfo,
                    alreadyOverDraft,
                    setAlreadyOverDraft,
                    showGenericError,
                    setShowGenericError,
                    showSecondGenericError,
                    setShowSecondGenericError,
                    showCannotSaveError,
                    setShowCannotSaveError,
                    showOnloadExceedError,
                    setShowOnloadExceedError,
                    showQuantityConsumedError,
                    setShowQuantityConsumedError,
                    showOverdraftModal,
                    setShowOverdraftModal,
                    overdraftStepChange,
                    saveSelectionChanges,
                    saveAssignChanges,
                }}
            >
                <SearchProductContext.Provider
                    value={{
                        //Search Products
                        searchedProducts,
                        setSearchedProducts,
                        searchedGridState,
                        setSearchedGridState,
                        searchedDataState,
                        setSearchedDataState
                    }}>
                    <SelectedProductContext.Provider value={{
                        //Selected Products
                        selectID,
                        selectedProducts,
                        setSelectedProducts,
                        unsavedChanges,
                        setUnsavedChanges,

                        //Selected Products (My Cart) Grid
                        selectedGridState,
                        setSelectedGridState,
                        selectHeaderCheck,
                        setSelectHeaderCheck,
                    }}>
                        <AssignedProductContext.Provider value={{
                            //Assign Products
                            assignedProducts,
                            setAssignedProducts,
                            unsavedAssignedChanges,
                            setUnsavedAssignedChanges,

                            //Assign Products to Host Grid
                            assignSelectID,
                            assignedGridState,
                            setAssignedGridState,
                            assignedGridStateRef,
                            assignedHeaderCheck,
                            setAssignedHeaderCheck,
                            disableArrayID,
                            sortByDisableID,
                            sortByDisableID2,
                            sortByDisableValue,
                            sortByDisableValue2,
                            enableSortByDisable,
                            setEnableSortByDisable,
                        }}>
                            <div className={"ksm-page-container"}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}>
                                    <div style={{
                                        whiteSpace: "nowrap"
                                    }} className={"k-h3"}>
                                        <Text
                                            textkey={fsaLicensingKey}
                                            textdefault={mainMessages[siteLanguageDefault][fsaLicensingKey]}
                                        />
                                    </div>
                                    <div style={{
                                        width: '27rem',
                                        paddingLeft: '10rem',
                                    }}>
                                        <KSMStepper
                                            stepperItems={stepperItems}
                                            stepValue={stepValue}
                                            changeStepActivation={changeStepActivation}
                                        />
                                    </div>
                                    <SummaryChart
                                        showOverdraft={showOverdraft}
                                        selectOverdraft={selectOverdraft}
                                        setSelectOverdraft={setSelectOverdraft}
                                    />
                                </div>

                                <div style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: '0.5rem'
                                }}>
                                    {stepValue === 0 && (
                                        <SelectProducts/>
                                    )}

                                    {stepValue === 1 && (
                                        <AssignProducts/>
                                    )}

                                    {stepValue === 2 && (
                                        <RequestDownload/>
                                    )}
                                </div>

                            </div>
                        </AssignedProductContext.Provider>
                    </SelectedProductContext.Provider>
                </SearchProductContext.Provider>
            </GeneralContext.Provider>
        </>
    )
}

export default FSARequestLicense;
